import CustomDialog from "components/CustomDialog"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Relay from "./Relay"
import { MC_SOFTWARE_TYPE } from "config/const"
import Ac from "./Ac"
import NullCheck from "components/NullCheck"
import Thermostat from "./Thermostat"
import { setShowLoading, setShowMassage } from "store/sysSlice"
import http from "../../../http"
import { apiUrl } from "config/settings"
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material"
import moment from "moment"

const Commands = ({ mcGuid, setMcGuid, setOpenBoostPower }) => {
    const dispatch = useDispatch()
    const sendTimerRef = useRef(null);
    const mcList = useSelector((state) => state?.mc.mcList);
    const isConnected = useSelector((state) => state.sys?.wsIsConnected);
    const [data, setData] = useState(null)
    const [offConfirmation, setOffConfirmation] = useState(null);

    useEffect(() => {
        if (mcGuid) {
            const findMc = mcList?.find(imc => imc?.guid === mcGuid)
            if (findMc) setData(findMc)
        }
    }, [mcList, mcGuid])

    const onClose = () => {
        if (sendTimerRef.current > 0) return
        setData(null)
        setMcGuid(null)
    }

    const isNowWithinWeeklyTime = (weeklyTime = []) => {
        const now = moment();
        const today = now.day() + 1;
        const nowMin = now.hours() * 60 + now.minutes();

        const getDayNumber = (dayValue) => {
            if (!dayValue) return null;

            if (typeof dayValue === "number") return dayValue;

            const map = {
                SUN: 1,
                SUNDAY: 1,
                MON: 2,
                MONDAY: 2,
                TUE: 3,
                TUESDAY: 3,
                WED: 4,
                WEDNESDAY: 4,
                THU: 5,
                THURSDAY: 5,
                FRI: 6,
                FRIDAY: 6,
                SAT: 7,
                SATURDAY: 7,
            };

            return map[String(dayValue).toUpperCase()] ?? null;
        };

        const toMinutes = (value) => {
            if (!value) return null;

            const m = moment(value);
            if (!m.isValid()) return null;

            return m.hours() * 60 + m.minutes();
        };

        if (!weeklyTime?.some(i => i?.isActive)) return true

        for (const item of weeklyTime) {
            if (!item?.isActive) continue;

            const dayNum = getDayNumber(item.day);
            const startMin = toMinutes(item.start);
            const endMin = toMinutes(item.end);

            if (!dayNum || startMin === null || endMin === null) continue;

            const crossesMidnight = endMin <= startMin;

            if (!crossesMidnight) {
                if (today === dayNum && nowMin >= startMin && nowMin < endMin) {
                    return true;
                }
            }

            else {
                if (today === dayNum && nowMin >= startMin) {
                    return true;
                }

                // חלק שני - היום הבא
                const nextDay = dayNum === 7 ? 1 : dayNum + 1;
                if (today === nextDay && nowMin < endMin) {
                    return true;
                }
            }
        }
        return false;
    };

    const checkWeeklyTime = async ({ callbackFunc }) => {
        if (!data?.isConnected) {
            return dispatch(setShowMassage({ massage: "MC_IS_DISCONNECTED", type: "error" }));
        }

        let isInsideWorkingHours = isNowWithinWeeklyTime(data?.weeklyTime || []);
        let nextOn;
        if (data?.hasControllerSchedule && (!data?.turnOnDate || Number(data?.boostPower) > 0)) {
            dispatch(setShowLoading(true));
            try {
                const response = await http.get(`${apiUrl}/mc/scheduleState/${data.guid}`);
                const context = response?.data?.data;
                if (!context || response.status >= 400) throw new Error("SCHEDULE_STATE_UNAVAILABLE");
                if (context.managed) {
                    isInsideWorkingHours = context.isInsideWorkingHours;
                    nextOn = context.nextOn || null;
                    if (data.turnOnDate && Number(data.boostPower) > 0 && context.isInsideWorkingHours) {
                        setOffConfirmation({ callbackFunc });
                        return;
                    }
                }
            } catch {
                dispatch(setShowMassage({ massage: "CS_ERROR_SCHEDULE_ERROR", type: "error" }));
                return;
            } finally { dispatch(setShowLoading(false)); }
        }

        if (!isInsideWorkingHours && !data?.turnOnDate) {
            setOpenBoostPower({ open: true, data: { ...data, scheduleNextOn: nextOn } });
            onClose();
            return;
        }

        callbackFunc();
    };

    return (
        <><CustomDialog
            open={data ? true : false}
            onClose={onClose}
            title={data?.name}
            maxWidth='xs'
            contentPadding={3}
        >
            <NullCheck valueForCheck={
                data?.softwareType === MC_SOFTWARE_TYPE.B1_0 ||
                data?.softwareType === MC_SOFTWARE_TYPE.B1_3 ||
                data?.softwareType === MC_SOFTWARE_TYPE.B2_0 ||
                data?.softwareType === MC_SOFTWARE_TYPE.B1_4 ||
                data?.softwareType === MC_SOFTWARE_TYPE.B1_9
            }>
                <Relay
                    isConnected={isConnected}
                    checkWeeklyTime={checkWeeklyTime}
                    setOpenBoostPower={setOpenBoostPower}
                    onClose={onClose}
                    data={data}
                />
            </NullCheck>

            <NullCheck valueForCheck={
                data?.softwareType === MC_SOFTWARE_TYPE.B1_1 ||
                data?.softwareType === MC_SOFTWARE_TYPE.B1_5 ||
                data?.softwareType === MC_SOFTWARE_TYPE.B1_8
            }>
                <Ac
                    checkWeeklyTime={checkWeeklyTime}
                    isConnected={isConnected}
                    sendTimerRef={sendTimerRef}
                    onClose={onClose}
                    data={data}
                />
            </NullCheck>

            <NullCheck valueForCheck={data?.softwareType === MC_SOFTWARE_TYPE.B1_6}>
                <Thermostat
                    checkWeeklyTime={checkWeeklyTime}
                    isConnected={isConnected}
                    sendTimerRef={sendTimerRef}
                    onClose={onClose}
                    data={data}
                    mcList={mcList}
                />
            </NullCheck>
        </CustomDialog>
        <Dialog open={!!offConfirmation} onClose={() => setOffConfirmation(null)} fullWidth maxWidth="xs" aria-labelledby="schedule-off-title">
            <DialogTitle id="schedule-off-title">כיבוי הבקר</DialogTitle>
            <DialogContent><DialogContentText>
                לפי לוח הזמנים, {data?.name} צריך להיות דלוק כרגע. האם לכבות אותו בכל זאת?
            </DialogContentText></DialogContent>
            <DialogActions sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 1, p: 2, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
                <Button fullWidth onClick={() => { const action = offConfirmation.callbackFunc; setOffConfirmation(null); action({ overrideSchedule: false }); }}>השאר לפי לוח הזמנים</Button>
                <Button fullWidth variant="contained" color="error" onClick={() => { const action = offConfirmation.callbackFunc; setOffConfirmation(null); action({ overrideSchedule: true }); }}>כבה</Button>
            </DialogActions>
        </Dialog></>
    )
}
export default Commands
